import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { loadUiComponent } from "../../../../graphql/queries/loadUiComponent";
import { getSchemaSlug } from "../configFieldSchemas";

/**
 * Hook that attempts to load jsonSchema and uiSchema from the
 * loadUiComponent GraphQL query for any configuration key.
 *
 * @param {string} configKey - Configuration key (e.g., 'generic_configuration')
 * @param {boolean} isOpen - Whether the drawer is currently open
 * @returns {{ jsonSchema: Object|null, uiSchema: Object|null, loading: boolean, failed: boolean }}
 */
export default function useLoadSchema(configKey, isOpen) {
  const slug = getSchemaSlug(configKey);

  const [jsonSchema, setJsonSchema] = useState(null);
  const [uiSchema, setUiSchema] = useState(null);
  const [loading, setLoading] = useState(Boolean(isOpen && configKey));
  const [failed, setFailed] = useState(false);

  const { refetch } = useQuery(loadUiComponent, {
    variables: { slug },
    skip: true,
    errorPolicy: "all",
  });

  useEffect(() => {
    if (!isOpen || !configKey) {
      setJsonSchema(null);
      setUiSchema(null);
      setFailed(false);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSchemas = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const res = await refetch({ slug });
        if (cancelled) return;
        if (
          res?.data?.loadUiComponent?.inputSchema &&
          res?.data?.loadUiComponent?.uiSchema
        ) {
          setJsonSchema(res.data.loadUiComponent.inputSchema);
          setUiSchema(res.data.loadUiComponent.uiSchema);
        } else {
          setFailed(true);
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSchemas();

    return () => {
      cancelled = true;
    };
  }, [configKey, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return { jsonSchema, uiSchema, loading, failed };
}
